import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import httpProvider from '../../provider/HttpProvider';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { isValidWebUrl, getUrlErrorMessage } from '../../utils/urlUtils';

const AddBookmarkForm = ({ onAddSuccess, onAddError }) => {
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleUrlSubmit = event => {
        event.preventDefault();
        setLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        if (!isValidWebUrl(url)) {
            const message = getUrlErrorMessage(url);
            setErrorMessage(message);
            onAddError(message);
            setLoading(false);
            return;
        }

        const encodedUrl = btoa(url);
        httpProvider
            .post(`/api/bookmarks/add?source=web&url=${encodedUrl}`)
            .then(() => {
                setLoading(false);
                setUrl('');
                setSuccessMessage('Bookmark added successfully');
                onAddSuccess();
            })
            .catch(error => {
                setLoading(false);
                setUrl('');
                // The error message is now pre-processed by the httpProvider interceptor
                const message = error.message || 'An error occurred';
                setErrorMessage(message);
                onAddError(message);
            });
    };

    const handleUrlChange = e => {
        setUrl(e.target.value);
        if (errorMessage) setErrorMessage('');
        if (successMessage) setSuccessMessage('');
    };

    return (
        <Box component="form" onSubmit={handleUrlSubmit} noValidate>
            <Grid container spacing={1} alignItems="flex-start">
                <Grid size={{ xs: 10 }}>
                    <TextField
                        id="addUrl"
                        value={url}
                        onChange={handleUrlChange}
                        variant="standard"
                        placeholder="Enter URL"
                        required
                        fullWidth
                        error={Boolean(errorMessage)}
                        helperText={successMessage || errorMessage}
                        FormHelperTextProps={{
                            sx: {
                                color: successMessage ? 'green' : undefined,
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 2 }}>
                    <Button
                        type="submit"
                        variant="text"
                        disabled={loading}
                        fullWidth
                        sx={{
                            color: 'white',
                            minWidth: 'auto',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                            '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.3)',
                            },
                        }}
                    >
                        {loading ? (
                            <HourglassEmptyIcon />
                        ) : (
                            <AddCircleOutlineIcon />
                        )}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

AddBookmarkForm.propTypes = {
    onAddSuccess: PropTypes.func,
    onAddError: PropTypes.func,
};

export default AddBookmarkForm;
